
/**
 * Environment and Feature Flag Management
 * Controls mock data visibility and environment-specific behavior
 */

export type AppEnvironment = 'development' | 'production' | 'beta' | 'demo';

export interface EnvironmentConfig {
  environment: AppEnvironment;
  showMockData: boolean;
  enableDemoMode: boolean;
  enableAnalyticsMocks: boolean;
  enableOnboarding: boolean;
}

class EnvironmentManager {
  private config: EnvironmentConfig;

  constructor() {
    this.config = this.initializeConfig();
  }

  private initializeConfig(): EnvironmentConfig {
    const environment = this.getEnvironmentType();

    return {
      environment,
      showMockData: environment === 'development' || environment === 'demo',
      enableDemoMode: environment === 'demo',
      enableAnalyticsMocks: environment === 'development',
      enableOnboarding: environment === 'beta' || environment === 'production',
    };
  }

  private getEnvironmentType(): AppEnvironment {
    // Check for explicit environment override
    const envOverride = localStorage.getItem('app_environment') as AppEnvironment;
    if (envOverride && ['development', 'production', 'beta', 'demo'].includes(envOverride)) {
      return envOverride;
    }

    // Check URL patterns
    const hostname = window.location.hostname;
    
    // Custom domain mapping - treat inveniocrm.com as beta environment
    if (hostname.includes('inveniocrm.com')) {
      return 'beta';
    }
    
    // Demo environments
    if (hostname.includes('demo') || hostname.includes('preview-demo')) {
      return 'demo';
    }
    
    // Beta environments (including Lovable preview environments)
    if (hostname.includes('beta') || 
        hostname.includes('preview-beta') ||
        hostname.includes('lovableproject.com') || 
        hostname.includes('preview--')) {
      return 'beta';
    }

    // Default based on import.meta.env
    return import.meta.env.PROD ? 'production' : 'development';
  }

  public getConfig(): EnvironmentConfig {
    return { ...this.config };
  }

  public shouldShowMockData(): boolean {
    return this.config.showMockData;
  }

  // Updated method with better logic for beta environments
  public shouldShowMockDataForUser(isDeveloper: boolean): boolean {
    const env = this.config.environment;
    
    // Never show mock data in beta or production for regular users
    if (env === 'beta' || env === 'production') {
      return false;
    }
    
    // Always show in development and demo environments
    if (env === 'development' || env === 'demo') {
      return true;
    }
    
    // Fallback: only show for developers
    return isDeveloper;
  }

  public isDemoMode(): boolean {
    return this.config.enableDemoMode;
  }

  public shouldShowAnalyticsMocks(): boolean {
    return this.config.enableAnalyticsMocks;
  }

  public shouldShowOnboarding(): boolean {
    return this.config.enableOnboarding;
  }

  public getEnvironment(): AppEnvironment {
    return this.config.environment;
  }

  // Allow runtime environment switching for demos
  public setEnvironment(env: AppEnvironment): void {
    localStorage.setItem('app_environment', env);
    this.config = this.initializeConfig();
  }

  // Debug method to help troubleshoot environment detection
  public getEnvironmentDebugInfo() {
    return {
      hostname: window.location.hostname,
      href: window.location.href,
      detectedEnvironment: this.config.environment,
      showMockData: this.config.showMockData,
      isDev: import.meta.env.DEV,
      isProd: import.meta.env.PROD,
      envOverride: localStorage.getItem('app_environment')
    };
  }
}

export const environmentManager = new EnvironmentManager();

// Convenience exports
export const shouldShowMockData = () => environmentManager.shouldShowMockData();
export const shouldShowMockDataForUser = (isDeveloper: boolean) => environmentManager.shouldShowMockDataForUser(isDeveloper);
export const isDemoMode = () => environmentManager.isDemoMode();
export const shouldShowAnalyticsMocks = () => environmentManager.shouldShowAnalyticsMocks();
export const shouldShowOnboarding = () => environmentManager.shouldShowOnboarding();
export const getEnvironment = () => environmentManager.getEnvironment();
export const getEnvironmentDebugInfo = () => environmentManager.getEnvironmentDebugInfo();
